import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-oauth-callback',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="flex items-center justify-center min-h-screen bg-surface-950">
            <div class="text-center">
                <div class="mb-4">
                    <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
                </div>
                <p class="text-surface-0 text-xl">Completing sign in...</p>
                @if (error) {
                    <div class="mt-4 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded text-red-200">
                        <p>{{ error }}</p>
                        <button class="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded" (click)="goToLogin()">
                            Return to Login
                        </button>
                    </div>
                }
            </div>
        </div>
    `
})
export class OAuthCallback implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private cloud = inject(CloudApiService);
    error: string | null = null;

    ngOnInit(): void {
        // Get token and refreshToken from query parameters
        const token = this.route.snapshot.queryParamMap.get('token');
        const refreshToken = this.route.snapshot.queryParamMap.get('refreshToken');
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
        const errorParam = this.route.snapshot.queryParamMap.get('error');

        if (errorParam) {
            this.error = this.getErrorMessage(errorParam);
            return;
        }

        if (!token || !refreshToken) {
            this.error = 'Missing authentication tokens. Please try again.';
            return;
        }

        try {
            // Set the auth tokens in the service
            this.cloud.setAuthFromOAuth(token, refreshToken);
            
            // Navigate to the return URL
            setTimeout(() => {
                this.router.navigateByUrl(returnUrl);
            }, 100);
        } catch (err) {
            console.error('OAuth callback error:', err);
            this.error = 'Failed to complete sign in. Please try again.';
        }
    }

    private getErrorMessage(error: string): string {
        switch (error) {
            case 'oauth_failed':
                return 'OAuth authentication failed. Please try again.';
            case 'oauth_missing_data':
                return 'Could not retrieve your profile information. Please ensure you have granted the necessary permissions.';
            case 'saml_failed':
                return 'SAML authentication failed. Please try again or contact your administrator.';
            case 'saml_missing_data':
                return 'Could not retrieve your profile information from SAML response. Please contact your administrator.';
            default:
                return 'An error occurred during sign in. Please try again.';
        }
    }

    goToLogin(): void {
        this.router.navigate(['/auth/login']);
    }
}
